import { ClientsService } from '@services/requests/housien-services/ClientsService';
import type { Client } from '@services/requests/housien-models/Client';
import type { ClientInput } from '@services/requests/housien-models/ClientInput';

type Pagination = {
  page?: string | number;
  limit?: string | number;
};

/**
 * Every narrowing the client listing understands, as query-ready strings.
 *
 * The address keys (`city`/`neighborhood`/`state`) ask where the client
 * LIVES; the `pref*` ones ask what they are LOOKING FOR. Two different
 * questions over two different value objects, so they stay two sets of
 * keys rather than one shared "location".
 */
type Filters = {
  type?: string;
  status?: string;
  search?: string;
  role?: string;
  broker?: string;
  originChannel?: string;
  withoutReturn?: string;
  importId?: string;
  incomeMin?: string;
  incomeMax?: string;
  city?: string;
  neighborhood?: string;
  state?: string;
  prefCity?: string;
  prefNeighborhood?: string;
  prefPriceMin?: string;
  prefPriceMax?: string;
  prefBusinessType?: string;
  prefPropertyKind?: string;
  prefBedroomsMin?: string;
  prefPetFriendly?: string;
  prefFurnished?: string;
  order?: string;
};

/** The filter keys carried straight through to the query string. */
const PASS_THROUGH_FILTERS: Array<keyof Filters> = [
  'type', 'status', 'search', 'role', 'broker', 'originChannel',
  'withoutReturn', 'importId', 'incomeMin', 'incomeMax', 'city',
  'neighborhood', 'state', 'prefCity', 'prefNeighborhood', 'prefPriceMin',
  'prefPriceMax', 'prefBusinessType', 'prefPropertyKind', 'prefBedroomsMin',
  'prefPetFriendly', 'prefFurnished', 'order',
];

type SearchParams = {
  pagination?: Pagination;
  filters?: Filters;
};

type SearchResponse = {
  data: Record<string, Client>;
  metadata: {
    pages: number;
    totalCount: number;
  };
};

type InsertParams = {
  body: ClientInput;
};

type UpdateParams = {
  id: string;
  body: ClientInput;
};

type ExcludeParams = {
  id: string;
};

/**
 * Indexes a list of clients into a lookup keyed by client_id.
 */
function arrayToObjectById(clients: Array<Client>): Record<string, Client> {
  const result: Record<string, Client> = {};
  clients.forEach((client) => {
    const clientId = client.metadata?.client_id;
    if (clientId) {
      result[String(clientId)] = client;
    }
  });
  return result;
}

/**
 * Normalizes pagination parameters.
 */
function parsePagination(params: SearchParams): { page: number; limit: number } {
  const page = params.pagination?.page ? Number(params.pagination.page) : 1;
  const limit = params.pagination?.limit ? Number(params.pagination.limit) : 10;
  return { page, limit };
}

/**
 * Extracts filter parameters.
 */
function parseFilters(params: SearchParams): Filters | undefined {
  if (!params.filters) return undefined;
  const given = params.filters;
  const result: Filters = {};
  PASS_THROUGH_FILTERS.forEach((key) => {
    if (given[key]) result[key] = given[key];
  });
  return result;
}

/**
 * Searches agency clients and returns id-keyed map.
 *
 * The generated `getAgencyClients` return type only declares `data`, but the
 * backend actually responds with pagination fields (`pages`, `items`) at the
 * top level alongside it — mirroring the properties search response. Those
 * are read off the raw payload rather than dropped, so callers can page
 * through the full result set and show an accurate total count.
 */
async function searchClients(
  agencyId: string,
  params: SearchParams,
): Promise<SearchResponse> {
  const { page, limit } = parsePagination(params);
  const filters = parseFilters(params);
  const response = await ClientsService.getAgencyClients(agencyId, page, limit, filters);
  const data = arrayToObjectById(response.data || []);
  const raw = response as unknown as { pages?: number; items?: number };
  const totalCount = typeof raw.items === 'number' ? raw.items : Object.keys(data).length;
  return { data, metadata: { pages: raw.pages ?? 1, totalCount } };
}

/**
 * Fetches a single client by id.
 */
function getClientById(agencyId: string, clientId: string): Promise<Client> {
  return ClientsService.getAgencyClient(agencyId, clientId);
}

/**
 * Creates a client.
 */
function insertClient(agencyId: string, params: InsertParams): Promise<Client> {
  return ClientsService.postAgencyClient(agencyId, params.body);
}

/**
 * Updates a client.
 */
function updateClient(agencyId: string, params: UpdateParams): Promise<Client> {
  return ClientsService.putAgencyClient(agencyId, params.id, params.body);
}

/**
 * Deletes a client.
 */
function excludeClient(agencyId: string, params: ExcludeParams): Promise<void> {
  return ClientsService.deleteAgencyClient(agencyId, params.id);
}

/**
 * Builds an agency-scoped client CRUD facade.
 */
function backendClientsCrud(agencyId: string) {
  return {
    search: (params: SearchParams) => searchClients(agencyId, params),
    getById: (clientId: string) => getClientById(agencyId, clientId),
    insert: (params: InsertParams) => insertClient(agencyId, params),
    update: (params: UpdateParams) => updateClient(agencyId, params),
    exclude: (params: ExcludeParams) => excludeClient(agencyId, params),
  };
}

export default backendClientsCrud;
